/* eslint-disable */
// @ts-nocheck
import { createFileRoute, useNavigate } from "@tanstack/react-router";
import { usePrivy } from "@privy-io/react-auth";
import { Fragment, useState, useTransition } from "react";
import { queryClient, useFindMatch, useSquadList, useStats } from "@/lib/hooks/useApi";
import { toast } from "sonner";
import { Spinner } from "@/components/ui/spinner";
import { Button } from "@/components/ui/button";
import { cn, humanizedDurationsMap, trophyAmountMap } from "@/lib/utils";
import AnimatedLoading from "@/components/AnimatedLoading";
import { ClockIcon, PlusIcon, TrendingDown, TrendingUp } from "lucide-react";
import { DirectionButton } from "@/components/DirectionButton";
import { DurationButton } from "@/components/DurationButton";
import { SquadButton } from "@/components/SquadButton";
import {
  Carousel,
  CarouselContent,
  CarouselItem,
} from "@/components/ui/carousel";
import FootballField from "@/assets/field.svg?react";

export const Route = createFileRoute("/(game)/pvp")({
  component: Pvp,
});

function Pvp() {
  const { user } = usePrivy();
  const navigate = useNavigate();
  const [isMutating, startTransition] = useTransition();
  const [activeMatchType, setActiveMatchType] = useState<
    "long" | "short" | null
  >(null);
  const { mutateAsync: findMatch } = useFindMatch();
  const { data: statsData } = useStats(user?.wallet?.address ?? "");
  const { data: squadList, isLoading } = useSquadList(statsData?.userId);

  const durationsInSeconds = ["60", "300", "3600", "14400", "43200", "86400"];
  const [current, setCurrent] = useState(1);
  const [selectedDuration, setSelectedDuration] = useState("60");

  const [isRedirecting, setIsRedirecting] = useState<"card" | "button" | null>(
    null
  );

  const handleAddNewSquadClick = (buttonType: "card" | "button") => {
    setIsRedirecting(buttonType);
    navigate({ to: "/create-squad" });
  };

  const handleDurationClick = () => {
    const currentIndex = durationsInSeconds.indexOf(selectedDuration);
    const nextIndex = (currentIndex + 1) % durationsInSeconds.length;

    if (durationsInSeconds[nextIndex] !== selectedDuration) {
      setSelectedDuration(durationsInSeconds[nextIndex]);
    }
  };

  if (isLoading || !user?.wallet?.address) {
    return <AnimatedLoading />;
  }

  if (!squadList?.length) {
    return (
      <Fragment>
        <div>No squad list</div>
      </Fragment>
    );
  }

  const handleMatchSetup = async (matchType: "long" | "short") => {
    setActiveMatchType(matchType);

    startTransition(async () => {
      try {
        const matchData = {
          squadId: squadList[current - 1].squadId,
          duration: Number(selectedDuration),
          matchType,
        };

        const match = await findMatch(matchData);

        queryClient.invalidateQueries({ queryKey: ["infiniteMatches"] });
        queryClient.invalidateQueries({ queryKey: ["match", match.matchId] });
        queryClient.invalidateQueries({ queryKey: ["squads"] });

        navigate({ to: `/match/${match.matchId}` });
      } catch (error: unknown) {
        console.error("Caught error:", error);

        let message = "An unexpected error occurred.";

        if (
          typeof error === "object" &&
          error !== null &&
          "response" in error
        ) {
          const err = error as {
            response?: { data?: { error?: string } };
          };

          message = err.response?.data?.error ?? message;
        }

        toast.error("Create match failed", {
          id: "create-match-error",
          description: message,
          position: "top-center",
        });
      } finally {
        setActiveMatchType(null);
      }
    });
  };

  return (
    <div className="flex flex-1 flex-col">
      <div className="flex-1 p-4 flex flex-col items-center">
        <div className="max-w-xs flex flex-col gap-4 items-center">
          <FootballField className="w-full" />
          <div className="w-full flex flex-row items-center justify-center gap-2">
            <DirectionButton
              direction="long"
              onClick={() => handleMatchSetup("long")}
              disabled={isMutating}
            />
            <DurationButton />
            <DirectionButton
              direction="short"
              onClick={() => handleMatchSetup("short")}
              disabled={isMutating}
            />
          </div>
          <div className="w-full flex flex-row items-center gap-3">
            <Carousel className="w-full">
              <CarouselContent>
                {squadList.map((squad, idx) => (
                  <CarouselItem key={squad.squadId} className="basis-auto">
                    <div
                      onClick={() => setCurrent(idx + 1)}
                      style={{
                        filter:
                          idx === current - 1 ? undefined : "grayscale(95%)",
                      }}
                    >
                      <SquadButton
                        squadName={squad.squadName}
                        pnl={squad.squadValueChanges24h}
                      />
                    </div>
                  </CarouselItem>
                ))}
                {/* Add Squad as the last item (never grayscaled) */}
                <CarouselItem className="basis-auto">
                  <Button
                    variant="add-squad"
                    aria-label="Add Squad"
                    onClick={() => handleAddNewSquadClick("button")}
                    disabled={isRedirecting === "button"}
                  />
                </CarouselItem>
              </CarouselContent>
            </Carousel>
          </div>
        </div>
      </div>
    </div>
  );
}
